import PageShell from "@/components/PageShell";
import { usePageContent } from "@/hooks/usePageContent";
import { useSEO } from "@/hooks/useSEO";
import { Building2, Camera, Video, BarChart3, Mail, Globe, Shield, Users } from "lucide-react";

const FEATURES = [
  { icon: Building2, key: "feature_1" },
  { icon: Camera, key: "feature_2" },
  { icon: Video, key: "feature_3" },
  { icon: Globe, key: "feature_4" },
  { icon: BarChart3, key: "feature_5" },
  { icon: Mail, key: "feature_6" },
  { icon: Shield, key: "feature_7" },
  { icon: Users, key: "feature_8" },
];

const DEFAULTS = {
  feature_1_title: "Property Listings", feature_1_desc: "Create beautiful, responsive property listing pages with full details, image galleries, video tours, and downloadable PDF brochures.",
  feature_2_title: "Professional Photography", feature_2_desc: "Upload and manage high-quality property images with progressive loading, automatic compression, and watermarking.",
  feature_3_title: "Video Marketing", feature_3_desc: "Embed YouTube, TikTok and Instagram videos directly on property pages. Support for YouTube Shorts and social media integration.",
  feature_4_title: "Website Builder", feature_4_desc: "Fully customisable website with CMS-managed content, adjustable colours, fonts, hero images, and navigation — all from the admin dashboard.",
  feature_5_title: "Analytics Dashboard", feature_5_desc: "Track property views, enquiries, phone calls, referrer domains, and campaign performance with historic charts and period filters.",
  feature_6_title: "Email Brochures", feature_6_desc: "Send professional property brochure emails directly to enquiry customers with property details, images, and agent information.",
  feature_7_title: "Multi-Tenant Security", feature_7_desc: "Each administrator has isolated data with Google SSO authentication. Agent accounts with password management and property-level access control.",
  feature_8_title: "Agent Management", feature_8_desc: "Manage multiple agents, assign properties, track performance, and allow agents to independently manage their listings through a dedicated dashboard.",
};

export default function AboutPage() {
  const { content: c } = usePageContent("about");

  const seoSummary = c?.about_seo_summary || "SellLocalHomes is a comprehensive property marketing platform that helps estate agents and property sellers create professional listings, manage enquiries, track analytics, and market properties through video campaigns and social media.";

  useSEO({
    seo_title: c?.about_seo_title || "About Us | SellLocalHomes",
    seo_description: seoSummary
  });

  return (
    <PageShell>
      <div className="min-h-screen">
      
      <section className="pt-12 md:pt-16 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          {/* SEO Summary (visible to crawlers, subtle on page) */}
          <div className="mb-10">
            <h1 className="font-serif text-3xl md:text-4xl font-medium mb-4">{c?.about_title || "About Us"}</h1>
            <p className="text-muted-foreground leading-relaxed">{seoSummary}</p>
          </div>

          {/* Mission */}
          <div className="bg-muted/30 border border-border p-6 mb-10">
            <h2 className="font-serif text-xl font-medium mb-3">{c?.about_mission_title || "Our Mission"}</h2>
            <p className="text-muted-foreground leading-relaxed">{c?.about_mission || "We empower property sellers and estate agents with professional marketing tools to showcase properties at their best. From stunning listings to targeted video campaigns, we provide everything needed to attract buyers and close sales faster."}</p>
          </div>

          {/* Features Grid */}
          <h2 className="font-serif text-2xl font-medium mb-6">{c?.about_features_title || "Platform Features"}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
            {FEATURES.map(({ icon: Icon, key }) => (
              <div key={key} className="border border-border p-5 space-y-2">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg"><Icon className="h-5 w-5 text-primary" /></div>
                  <h3 className="font-medium">{c?.[`${key}_title`] || DEFAULTS[`${key}_title`]}</h3>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">{c?.[`${key}_desc`] || DEFAULTS[`${key}_desc`]}</p>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="text-center py-8 bg-muted/30 border border-border">
            <h2 className="font-serif text-xl font-medium mb-2">{c?.about_cta_title || "Ready to get started?"}</h2>
            <p className="text-muted-foreground mb-4">{c?.about_cta_desc || "Contact us today to learn how we can help market your property."}</p>
          </div>
        </div>
      </section>
      </div>
    </PageShell>
  );
}
